import {Component, type ErrorInfo, type ReactNode} from 'react';
import {IS_DEVELOPMENT} from 'utils/general';
import {logProductionErrorNoHook} from './utils';

/** Longest component stack kept in a log entry. */
const MAX_COMPONENT_STACK_CHARS = 2000;

/** Areas already logged on this page load. */
const loggedAreas = new Set<string>();

/**
 * Logs that one part of a page failed to render, once per area per page
 * load, so a part that fails on every render cannot flood `errorLogs`.
 *
 * @param args.area - Which part failed, e.g. `student-home-map`.
 * @param args.error - What the part threw.
 * @param args.componentStack - React's component stack for the failure.
 *     Omitted or null, the entry carries none.
 * @return Nothing; the write happens in the background.
 * @throws Never.
 */
export function logPartFailure({
  area,
  error,
  componentStack,
}: {
  area: string;
  error: unknown;
  componentStack?: string | null;
}): void {
  if (loggedAreas.has(area)) {
    return;
  }
  loggedAreas.add(area);
  const thrown = error instanceof Error ? error : new Error(String(error));
  if (IS_DEVELOPMENT) {
    console.warn(`[part-error] ${area} failed to render`, thrown);
    return;
  }
  void logProductionErrorNoHook({
    error: thrown,
    additionalMetadata: {
      source: 'PartErrorBoundary',
      area,
      componentStack: (componentStack ?? '').slice(
        0,
        MAX_COMPONENT_STACK_CHARS,
      ),
    },
  }).catch((loggingError: unknown) => {
    console.debug(`[part-error] could not log ${area}`, loggingError);
  });
}

/** Props for {@link PartErrorBoundary}. */
interface PartErrorBoundaryProps {
  /** Names the part in the log entry, e.g. `student-home-map`. */
  area: string;
  /** The part. */
  children: ReactNode;
  /** Shown in the part's place once it has thrown. Omitted, nothing is. */
  fallback?: ReactNode;
}

/** State of {@link PartErrorBoundary}. */
interface PartErrorBoundaryState {
  /** Whether the part has thrown. */
  hasError: boolean;
}

/**
 * Keeps one part of a page from taking the whole page down: when the part
 * throws while rendering, the boundary shows `fallback` in its place and
 * logs the failure (see {@link logPartFailure}). It does not catch errors
 * from event handlers or promises.
 */
export class PartErrorBoundary extends Component<
  PartErrorBoundaryProps,
  PartErrorBoundaryState
> {
  state: PartErrorBoundaryState = {hasError: false};

  static getDerivedStateFromError(): PartErrorBoundaryState {
    return {hasError: true};
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    logPartFailure({
      area: this.props.area,
      error,
      componentStack: info.componentStack,
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}
